import { useEffect, useRef } from "react";
import { diasAte, formatarData, type CursoDoAluno } from "../lib/dados";
import { MarcaDoCurso } from "./ui";

// Todas as vigências do aluno, num lugar só.
//
// O card de "Vigência mais próxima" na tela inicial mostra uma data — a que
// vence antes — porque é a que exige ação. Mas quem tem dez cursos precisa
// poder conferir os outros nove sem abrir curso por curso, e o pedido de Victor
// foi exatamente esse: clicar no card e ver a lista.
//
// `<dialog>` nativo, e não uma `div` com overlay: ele já traz o fechamento por
// Esc, a armadilha de foco, o `aria-modal` e o backdrop — quatro coisas que uma
// div exige reimplementar, e que costumam ficar pela metade.
export default function ModalVigencias({
  cursos,
  aberto,
  aoFechar,
}: {
  cursos: CursoDoAluno[];
  aberto: boolean;
  aoFechar: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (aberto && !dlg.open) dlg.showModal();
    if (!aberto && dlg.open) dlg.close();
  }, [aberto]);

  // Ordenado como o aluno pensa: o que vence primeiro no topo, e o vitalício —
  // que não vence — no fim. Ordem alfabética aqui esconderia justamente o que a
  // tela existe para mostrar.
  const ordenados = [...cursos].sort((a, b) => {
    if (a.vitalicio !== b.vitalicio) return a.vitalicio ? 1 : -1;
    if (a.vitalicio) return a.nome.localeCompare(b.nome, "pt-BR");
    return (a.fim_efetivo ?? "").localeCompare(b.fim_efetivo ?? "");
  });

  return (
    <dialog
      ref={ref}
      onClose={aoFechar}
      // Clique no backdrop fecha: o alvo do clique é o próprio <dialog> apenas
      // quando se acerta a área de fora do conteúdo.
      onClick={(e) => {
        if (e.target === ref.current) aoFechar();
      }}
      aria-labelledby="titulo-vigencias"
      className="m-auto w-[min(94vw,42rem)] rounded-[1.125rem] bg-superficie p-0 text-tinta shadow-2xl backdrop:bg-azul/70 backdrop:backdrop-blur-sm"
    >
      <div className="flex items-start justify-between gap-4 border-b border-borda p-6">
        <div>
          <h2 id="titulo-vigencias" className="font-display text-xl font-bold text-titulo">
            Os seus acessos
          </h2>
          <p className="mt-1 text-sm text-cinza">
            Até quando cada curso fica disponível para você.
          </p>
        </div>
        <button
          onClick={aoFechar}
          aria-label="Fechar"
          className="grid size-11 shrink-0 place-items-center rounded-full text-cinza transition hover:bg-chip hover:text-titulo"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="size-5" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <ul className="max-h-[60vh] overflow-y-auto p-3">
        {ordenados.map((c) => (
          // Sem `flex-wrap`: com nome de pós longo a data caía para a linha de
          // baixo e ficava alinhada à esquerda, então algumas linhas tinham a
          // data à direita e outras embaixo. O nome quebra em duas linhas; a
          // data fica sempre no mesmo lugar, que é como se compara uma lista.
          <li
            key={c.slug}
            className="flex items-center justify-between gap-4 rounded-xl px-3 py-3 hover:bg-chip/50"
          >
            <div className="flex min-w-0 items-center gap-3">
              {/* A marca de quem oferece: escudo da FBC nos cursos da faculdade,
                  símbolo do CF nos cursos livres. Antes só a FBC tinha selo, e a
                  ausência no outro lado parecia falta de dado em vez de
                  distinção. */}
              <MarcaDoCurso faculdade={c.faculdade} />
              <div className="min-w-0">
                <p className="font-display font-semibold text-titulo">{c.nome}</p>
                <p className="mt-0.5 text-sm text-cinza">
                  {c.faculdade ? "Faculdade FBC" : "Contabilidade Facilitada"}
                  {c.produtos.length > 1 && " · 2 compras somadas"}
                </p>
              </div>
            </div>
            <Estado curso={c} />
          </li>
        ))}
      </ul>

      <p className="border-t border-borda px-6 py-4 text-xs text-cinza">
        A data é a do seu acesso ao curso. Comprou o mesmo curso mais de uma vez? Os prazos se somam, e
        aqui aparece o resultado.
      </p>
    </dialog>
  );
}

function Estado({ curso }: { curso: CursoDoAluno }) {
  if (curso.vitalicio) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-tile px-3 py-1.5 text-sm font-semibold text-link">
        Vitalício <span aria-hidden="true">∞</span>
      </span>
    );
  }

  if (!curso.fim_efetivo) return null;

  const dias = diasAte(curso.fim_efetivo);
  const urgente = dias <= 30;

  return (
    <span className="shrink-0 text-right">
      <span
        className={`block text-sm font-semibold tabular-nums ${urgente ? "text-alerta-tinta" : "text-titulo"}`}
      >
        até {formatarData(curso.fim_efetivo)}
      </span>
      <span className={`block text-xs tabular-nums ${urgente ? "text-alerta-tinta" : "text-cinza"}`}>
        {dias <= 0
          ? "vence hoje"
          : dias === 1
            ? "falta 1 dia"
            : dias < 60
              ? `faltam ${dias} dias`
              : `faltam ${Math.round(dias / 30)} meses`}
      </span>
    </span>
  );
}
